import * as React from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { BumpTrainingSubNav } from "@/components/bump_training/BumpTrainingSubNav"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { MultiSelect } from "@/components/ui/multi-select"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import type { PageProps } from "@/types/inertia"
import type { BumpCourseOption, PeriodOption, TrainingDay } from "@/types/bump_training"

const DESCRIPTION =
  "Record the Building Union Power courses you delivered on each day of a two-week period. Pick a period, choose the courses for any day, and save."

/** { "2026-08-16": ["3", "7"], ... } — MultiSelect speaks strings, Rails wants ids. */
type DaySelections = Record<string, string[]>

function initialSelections(days: TrainingDay[]): DaySelections {
  return Object.fromEntries(days.map((day) => [day.date, day.course_ids.map(String)]))
}

export default function BumpTrainingForm({
  period,
  periods,
  courses,
  days,
  can_edit,
}: {
  period: PeriodOption
  periods: PeriodOption[]
  courses: BumpCourseOption[]
  days: TrainingDay[]
  /** False with Read only: the days can be read and the period changed, nothing saved. */
  can_edit: boolean
}) {
  const errors = usePage<PageProps>().props.errors ?? {}
  const readOnly = !can_edit
  const [pending, setPending] = React.useState<string | null>(null)

  const { data, setData, transform, patch, processing, isDirty } = useForm({
    period: period.value,
    // Grouped under one key rather than spread flat, so the fourteen days stay
    // indexable by date without fighting the form helper's typing.
    days: initialSelections(days),
  })

  useBlockAutoReload(isDirty)

  // A different period is a different page, so the day map has to be re-seeded
  // from props rather than keeping the one the form was built with.
  React.useEffect(() => {
    setData({ period: period.value, days: initialSelections(days) })
    setPending(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.value])

  const options = React.useMemo(
    () =>
      courses.map((course) => ({
        value: String(course.id),
        label: course.code === course.label ? course.code : course.code + " — " + course.label,
      })),
    [courses],
  )

  // The trigger shows the code alone, or a count. Full course names are long
  // enough that two of them truncate to nothing useful, and the chips beneath
  // already spell them out — which is also how the legacy dropdown read.
  const codesById = React.useMemo(
    () => Object.fromEntries(courses.map((course) => [String(course.id), course.code])),
    [courses],
  )

  const periodOptions = React.useMemo(
    () => periods.map((option) => ({ value: option.value, label: option.label })),
    [periods],
  )

  const recordedDays = Object.values(data.days).filter((ids) => ids.length > 0).length

  const setDay = (date: string, ids: string[]) =>
    setData("days", { ...data.days, [date]: ids })

  // Changing the period navigates on an untouched form. With unsaved work it
  // does not: silently discarding what somebody just picked is worse than
  // asking them to save or say so first.
  const choosePeriod = (value: string) => {
    if (value === data.period) return
    if (isDirty) {
      setPending(value)
      return
    }
    router.get("/bump_training", { period: value }, { preserveState: false })
  }

  const pendingLabel = periods.find((option) => option.value === pending)?.label

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (readOnly) return
    transform((form) => ({ bump_training: form }))
    patch("/bump_training", { preserveScroll: true })
  }

  const weekOne = days.filter((day) => day.week === 1)
  const weekTwo = days.filter((day) => day.week === 2)

  return (
    <>
      <Head title="BUMP Training Tracker">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="BUMP Training Tracker" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="BUMP Training Tracker"
          description="The Building Union Power courses you delivered, day by day."
          tabs={<BumpTrainingSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          {errors.base && (
            <div className="callout callout-danger mb-4" role="alert">
              {errors.base}
            </div>
          )}

          {readOnly && (
            <div className="callout callout-signal mb-4" role="status">
              <p className="text-sm">
                You have read-only access to BUMP Training. You can look through your
                recorded days, but not change them.
              </p>
            </div>
          )}

          <form onSubmit={submit}>
            <div className="max-w-sm">
              <label htmlFor="period">Period</label>
              <div className="mt-1">
                <Combobox
                  id="period"
                  options={periodOptions}
                  value={data.period}
                  onChange={choosePeriod}
                  searchPlaceholder="Jump to a month..."
                  triggerLabel="Choose a two-week period"
                />
              </div>
            </div>

            {pending && (
              <div className="callout callout-signal mt-4" role="status">
                You have unsaved changes on {period.label}. Save them first, or{" "}
                <Link
                  href={"/bump_training?period=" + pending}
                  preserveState={false}
                  className="font-medium"
                >
                  open {pendingLabel} and lose them
                </Link>
                .{" "}
                <button
                  type="button"
                  className="toggle-button ml-1"
                  onClick={() => setPending(null)}
                >
                  Stay here
                </button>
              </div>
            )}

            <DayWeek
              heading="Week 1"
              days={weekOne}
              options={options}
              codesById={codesById}
              selections={data.days}
              onChange={setDay}
              disabled={readOnly}
            />
            <DayWeek
              heading="Week 2"
              days={weekTwo}
              options={options}
              codesById={codesById}
              selections={data.days}
              onChange={setDay}
              disabled={readOnly}
            />

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
              {!readOnly && (
                <Button type="submit" disabled={processing || !isDirty}>
                  {processing ? "Saving..." : "Save this period"}
                </Button>
              )}
              <p className="text-sm text-ink-muted">
                {recordedDays === 0
                  ? "No days recorded in this period yet."
                  : recordedDays + " of 14 days recorded."}
                {!readOnly && " Clearing every course from a day removes that day."}
              </p>
            </div>
          </form>
        </div>
      </AppShell>
    </>
  )
}

function DayWeek({
  heading,
  days,
  options,
  codesById,
  selections,
  onChange,
  disabled,
}: {
  heading: string
  days: TrainingDay[]
  options: { value: string; label: string }[]
  codesById: Record<string, string>
  selections: DaySelections
  onChange: (date: string, ids: string[]) => void
  disabled: boolean
}) {
  return (
    <section className="mt-8">
      <h2>{heading}</h2>
      <ul className="mt-3 divide-y divide-hairline border-y border-hairline">
        {days.map((day) => {
          const chosen = selections[day.date] ?? []

          return (
            <li
              key={day.date}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6"
            >
              <label htmlFor={"day-" + day.date} className="sm:pt-2">
                {day.day_name}
                <span className="ml-2 font-normal text-ink-muted">{day.day_label}</span>
              </label>
              <div className="sm:w-80 sm:shrink-0">
                <MultiSelect
                  id={"day-" + day.date}
                  options={options}
                  values={chosen}
                  disabled={disabled}
                  onChange={(values) => onChange(day.date, values)}
                  placeholder="No training"
                  searchPlaceholder="Filter courses..."
                  summarize={(count) => (count === 1 ? codesById[chosen[0]] : count + " courses")}
                  triggerLabel={"Courses delivered on " + day.day_name + " " + day.day_label}
                  selectionLabel={"Courses on " + day.day_name + " " + day.day_label}
                />
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
